/**
 * Compatibility Rules Utility
 * Handles rule checking and enforcement for product configurations
 */

/**
 * Check if an option belongs to a rule constraint (direct option or option group)
 * @param {Object} option - The option to check
 * @param {Number|null} ruleOptionId - Direct option ID from rule
 * @param {Number|null} ruleOptionGroupId - Option group ID from rule
 * @returns {Boolean}
 */
export function optionMatchesRuleConstraint(
  option,
  ruleOptionId,
  ruleOptionGroupId
) {
  // Check direct option ID match
  if (ruleOptionId && option.id === ruleOptionId) {
    return true;
  }

  // Check option group membership
  if (
    ruleOptionGroupId &&
    option.option_groups &&
    option.option_groups.includes(ruleOptionGroupId)
  ) {
    return true;
  }

  return false;
}

/**
 * Check if any option in configuration matches rule constraint
 * @param {Object} configuration - Current configuration state
 * @param {Number|null} ruleOptionId - Direct option ID from rule
 * @param {Number|null} ruleOptionGroupId - Option group ID from rule
 * @returns {Boolean}
 */
export function isOptionSelectedInConfiguration(
  configuration,
  ruleOptionId,
  ruleOptionGroupId
) {
  for (const slotConfig of Object.values(configuration)) {
    const selectedOptions = Array.isArray(slotConfig)
      ? slotConfig
      : [slotConfig];

    for (const selectedOption of selectedOptions) {
      if (
        optionMatchesRuleConstraint(
          selectedOption,
          ruleOptionId,
          ruleOptionGroupId
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Find all options that match a rule constraint in available slots
 * @param {Array} configurableSlots - All configurable slots with options
 * @param {Number|null} ruleOptionId - Direct option ID from rule
 * @param {Number|null} ruleOptionGroupId - Option group ID from rule
 * @returns {Array} - Array of {slot, option} objects
 */
export function findOptionsMatchingConstraint(
  configurableSlots,
  ruleOptionId,
  ruleOptionGroupId
) {
  const matches = [];

  for (const slot of configurableSlots) {
    if (!slot.options) continue;

    for (const option of slot.options) {
      if (
        optionMatchesRuleConstraint(option, ruleOptionId, ruleOptionGroupId)
      ) {
        matches.push({ slot, option });
      }
    }
  }

  return matches;
}

/**
 * Get applicable rules for a specific option
 * @param {Object} option - The option to check rules for
 * @param {Array} compatibilityRules - All compatibility rules
 * @returns {Array} - Applicable rules
 */
export function getRulesForOption(option, compatibilityRules) {
  return compatibilityRules.filter((rule) => {
    // Check if option is the "first" part of any rule
    const isFirstOption = optionMatchesRuleConstraint(
      option,
      rule.first_option?.id || null,
      rule.first_option_group?.id || null
    );

    // Check if option is the "second" part of any rule
    const isSecondOption = optionMatchesRuleConstraint(
      option,
      rule.second_option?.id || null,
      rule.second_option_group?.id || null
    );

    return isFirstOption || isSecondOption;
  });
}

/**
 * Check if an option is compatible with current configuration
 * @param {Object} option - The option to check
 * @param {Object} configuration - Current configuration state
 * @param {Array} compatibilityRules - All compatibility rules
 * @returns {Object} - {compatible: Boolean, reason: String|null, rule: Object|null}
 */
export function checkOptionCompatibility(
  option,
  configuration,
  compatibilityRules
) {
  // If no rules, everything is compatible
  if (!compatibilityRules.length) {
    return { compatible: true, reason: null, rule: null };
  }

  const applicableRules = getRulesForOption(option, compatibilityRules);

  for (const rule of applicableRules) {
    const isFirstOption = optionMatchesRuleConstraint(
      option,
      rule.first_option?.id || null,
      rule.first_option_group?.id || null
    );

    const isSecondOption = optionMatchesRuleConstraint(
      option,
      rule.second_option?.id || null,
      rule.second_option_group?.id || null
    );

    // Check if the counterpart is selected
    let counterpartSelected = false;

    if (isFirstOption) {
      // Check if second option/group is selected
      counterpartSelected = isOptionSelectedInConfiguration(
        configuration,
        rule.second_option?.id || null,
        rule.second_option_group?.id || null
      );
    } else if (isSecondOption) {
      // Check if first option/group is selected
      counterpartSelected = isOptionSelectedInConfiguration(
        configuration,
        rule.first_option?.id || null,
        rule.first_option_group?.id || null
      );
    }

    // Apply rule logic
    switch (rule.rule_type) {
      case "incompatible":
        if (counterpartSelected) {
          return {
            compatible: false,
            reason: rule.description || "Incompatible with current selection",
            rule,
          };
        }
        break;

      case "required_first":
        // If this option is in the "first" position and requires the second to be selected first
        if (isFirstOption && !counterpartSelected) {
          return {
            compatible: false,
            reason: rule.description || "Required option not yet selected",
            rule,
          };
        }
        break;

      case "required":
        // The "required" type means if first is selected, second must be too
        // But this doesn't block selection, it triggers auto-selection
        // So it doesn't affect compatibility check
        break;

      case "suggested":
        // Suggested rules don't block compatibility
        break;
    }
  }

  return { compatible: true, reason: null, rule: null };
}

/**
 * Get rules that should trigger when an option is selected
 * @param {Object} option - The selected option
 * @param {Array} compatibilityRules - All compatibility rules
 * @returns {Array} - Rules that should trigger
 */
export function getTriggeredRules(option, compatibilityRules) {
  const triggered = [];

  for (const rule of compatibilityRules) {
    const isFirstOption = optionMatchesRuleConstraint(
      option,
      rule.first_option?.id || null,
      rule.first_option_group?.id || null
    );

    // "suggested" rules only trigger in one direction (first → second)
    if (rule.rule_type === "suggested" && isFirstOption) {
      triggered.push(rule);
    }

    // "required" rules only trigger in one direction (first → second)
    if (rule.rule_type === "required" && isFirstOption) {
      triggered.push(rule);
    }
  }

  return triggered;
}

/**
 * Check if an option can be deselected
 * @param {Object} option - The option to check
 * @param {Object} configuration - Current configuration state
 * @param {Array} compatibilityRules - All compatibility rules
 * @returns {Object} - {canDeselect: Boolean, reason: String|null}
 */
export function canDeselectOption(option, configuration, compatibilityRules) {
  // Check if this option is required by another selected option
  for (const rule of compatibilityRules) {
    if (rule.rule_type !== "required") continue;

    // Check if this option is the "second" (required) option
    const isRequiredOption = optionMatchesRuleConstraint(
      option,
      rule.second_option?.id || null,
      rule.second_option_group?.id || null
    );

    if (!isRequiredOption) continue;

    // Check if the "first" (requiring) option is selected
    const requiringOptionSelected = isOptionSelectedInConfiguration(
      configuration,
      rule.first_option?.id || null,
      rule.first_option_group?.id || null
    );

    if (requiringOptionSelected) {
      return {
        canDeselect: false,
        reason:
          rule.description ||
          "This option is required by another selected option",
      };
    }
  }

  return { canDeselect: true, reason: null };
}
